const overlaps = (a, b, gap) => a.x < b.x + b.width + gap && a.x + a.width + gap > b.x && a.y < b.y + b.height + gap && a.y + a.height + gap > b.y;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export function fitLabelText(name, measure, maxWidth) {
  const full = String(name).trim();
  if (measure(full) <= maxWidth) return { text: full, width: measure(full) };
  const letters = Array.from(full);
  while (letters.length && measure(`${letters.join("")}…`) > maxWidth) letters.pop();
  const text = `${letters.join("")}…`;
  return { text, width: measure(text) };
}

// Layout only: this never changes player coordinates or tactical data.
export function layoutPlayerLabels(labels, obstacles, bounds, gap = 1, previous = []) {
  const placed = [];
  const oldById = new Map(previous.map((p) => [p.id, p]));
  for (const label of labels) {
    const width = Math.min(label.width, bounds.width);
    const height = label.height;
    if (height > bounds.height) continue;
    const rectAt = (cx, cy) => ({
      x: clamp(cx - width / 2, bounds.x, bounds.x + bounds.width - width),
      y: clamp(cy - height / 2, bounds.y, bounds.y + bounds.height - height),
      width, height,
    });
    const clearNames = (rect) => !placed.some((p) => overlaps(rect, p, gap));
    const clearAll = (rect) => clearNames(rect) && !obstacles.some((o) => overlaps(rect, o, gap));
    const direction = label.above ? -1 : 1;
    const candidates = [];
    let chosen;
    const old = oldById.get(label.id);
    if (old && old.width === width && old.height === height) {
      const retained = rectAt(old.x + width / 2 + label.anchorX - old.anchorX, old.y + height / 2 + label.anchorY - old.anchorY);
      if (clearAll(retained)) chosen = retained;
    }
    const step = height + gap;
    const limit = Math.hypot(bounds.width, bounds.height);
    for (let distance = label.offset; distance <= limit && !chosen; distance += step) {
      for (const [dx, dy] of [[0, direction], [0, -direction], [1, 0], [-1, 0], [0.7, direction * 0.7], [-0.7, direction * 0.7], [0.7, -direction * 0.7], [-0.7, -direction * 0.7]]) {
        const rect = rectAt(label.anchorX + dx * distance, label.anchorY + dy * distance);
        candidates.push(rect);
        if (clearAll(rect)) { chosen = rect; break; }
      }
    }
    // Search the remaining free space for especially tight groups near a touchline.
    if (!chosen) {
      const grid = [];
      for (let y = bounds.y; y + height <= bounds.y + bounds.height; y += step) {
        for (let x = bounds.x; x + width <= bounds.x + bounds.width; x += Math.max(step, width / 2)) {
          grid.push({ x, y, width, height });
        }
      }
      grid.sort((a, b) => Math.hypot(a.x + width / 2 - label.anchorX, a.y + height / 2 - label.anchorY) - Math.hypot(b.x + width / 2 - label.anchorX, b.y + height / 2 - label.anchorY));
      chosen = grid.find(clearAll) || [...candidates, ...grid].find(clearNames);
    }
    if (!chosen) continue;
    placed.push({ ...label, ...chosen,
      lineX: clamp(label.anchorX, chosen.x, chosen.x + width),
      lineY: clamp(label.anchorY, chosen.y, chosen.y + height),
    });
  }
  return placed;
}
